'use client'

import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { updateProjectAction, pushProjectToGitHub } from '@/app/actions/projects'
import { Loader2, Github } from 'lucide-react'
import { useRouter } from 'next/navigation'

interface Project {
    name: string
    type?: 'local' | 'github'
    branch: string
    envVars?: string
}

interface SettingsDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    project: Project
}

export function SettingsDialog({ open, onOpenChange, project }: SettingsDialogProps) {
    const router = useRouter()
    const [branch, setBranch] = useState(project.branch)
    const [envVars, setEnvVars] = useState(project.envVars)
    const [saving, setSaving] = useState(false)

    // GitHub linking state
    const [repoName, setRepoName] = useState(project.name)
    const [linking, setLinking] = useState(false)
    const [linkError, setLinkError] = useState('')
    const [linkSuccess, setLinkSuccess] = useState('')

    const isLocal = project.type === 'local'

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setBranch(project.branch)
            setEnvVars(project.envVars)
            setRepoName(project.name)
            setLinkError('')
            setLinkSuccess('')
        }
    }, [open, project])

    const handleSave = async () => {
        setSaving(true)
        try {
            await updateProjectAction(project.name, { branch, envVars })
            onOpenChange(false)
        } catch (error) {
            console.error('Failed to update project:', error)
        } finally {
            setSaving(false)
        }
    }

    const handlePushToGitHub = async () => {
        if (!repoName) return

        setLinking(true)
        setLinkError('')
        setLinkSuccess('')
        try {
            const result = await pushProjectToGitHub(project.name, repoName)
            if (result.success) {
                setLinkSuccess(`Created and pushed to ${result.repoFullName}`)
                setTimeout(() => {
                    onOpenChange(false)
                    router.refresh()
                }, 1500)
            } else {
                setLinkError(result.error)
            }
        } catch (error) {
            console.error('Failed to push to GitHub:', error)
            setLinkError(error instanceof Error ? error.message : 'Failed to push to GitHub')
        } finally {
            setLinking(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Project Settings</DialogTitle>
                    <DialogDescription>
                        Update configuration for <strong>{project.name}</strong>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* GitHub linking for local projects */}
                    {isLocal && (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                            <div className="flex items-center gap-2">
                                <Github className="h-4 w-4" />
                                <Label className="font-medium">Push to GitHub</Label>
                            </div>
                            <div className="space-y-2">
                                <Input
                                    value={repoName}
                                    onChange={(e) => setRepoName(e.target.value)}
                                    placeholder="repository-name"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                />
                                {linkError && (
                                    <p className="text-sm text-red-500">{linkError}</p>
                                )}
                                {linkSuccess && (
                                    <p className="text-sm text-green-500">{linkSuccess}</p>
                                )}
                                <Button
                                    onClick={handlePushToGitHub}
                                    disabled={!repoName || linking}
                                    variant="secondary"
                                    className="w-full"
                                >
                                    {linking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Create Repo & Push
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Creates a new private GitHub repo and pushes all project files.
                            </p>
                        </div>
                    )}

                    {!isLocal && (
                        <div className="space-y-2">
                            <Label htmlFor="branch">Branch</Label>
                            <Input
                                id="branch"
                                value={branch}
                                onChange={(e) => setBranch(e.target.value)}
                                placeholder="main"
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="env">Environment Variables (.env format)</Label>
                        <Textarea
                            id="env"
                            value={envVars}
                            onChange={(e) => setEnvVars(e.target.value)}
                            placeholder="KEY=VALUE"
                            rows={6}
                            className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                            These variables override the ones in your repository's .env file.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Save Changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

